import { useEffect, useRef, useState, useMemo } from "react";
import { toast } from "sonner";
import { Undo2, Trash2, Circle } from "lucide-react";
import { api } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { AdSlot } from "@/components/AdSlot";

const LS_RESULTS = "ltp_shoe_results";
const LS_SID = "ltp_shoe_sid";
const BEAD_ROWS = 6;
const BIG_ROWS = 6;

const META = {
  B: { label: "Banker", color: "bg-banker", ring: "ring-banker", text: "text-banker" },
  P: { label: "Player", color: "bg-player", ring: "ring-player", text: "text-player" },
  T: { label: "Tie", color: "bg-tie", ring: "ring-tie", text: "text-tie" },
};

export default function ShoeBoard() {
  const [results, setResults] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(LS_RESULTS)) || [];
    } catch {
      return [];
    }
  });
  const sidRef = useRef(localStorage.getItem(LS_SID));
  const syncTimer = useRef(null);

  useSeo({
    title: "Shoe Pattern Board — Baccarat Tracker | Licence to Play",
    description:
      "Free interactive baccarat shoe tracker. Log Banker, Player and Tie results on a bead plate and big road roadmap. Your session is saved automatically.",
    keywords: "baccarat road, big road, bead plate, baccarat tracker, shoe tracker",
  });

  // Ensure a backend session exists
  useEffect(() => {
    if (!sidRef.current) {
      api
        .post("/shoe/sessions", { results })
        .then((r) => {
          sidRef.current = r.data.id;
          localStorage.setItem(LS_SID, r.data.id);
        })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist locally + debounced backend sync
  useEffect(() => {
    localStorage.setItem(LS_RESULTS, JSON.stringify(results));
    if (syncTimer.current) clearTimeout(syncTimer.current);
    syncTimer.current = setTimeout(() => {
      if (sidRef.current) {
        api.put(`/shoe/sessions/${sidRef.current}`, { results }).catch(() => {});
      }
    }, 800);
    return () => clearTimeout(syncTimer.current);
  }, [results]);

  const add = (r) => setResults((p) => [...p, r]);
  const undo = () => setResults((p) => p.slice(0, -1));
  const reset = () => {
    setResults([]);
    toast.success("Shoe cleared. Fresh session.");
  };

  const stats = useMemo(() => {
    const b = results.filter((r) => r === "B").length;
    const p = results.filter((r) => r === "P").length;
    const t = results.filter((r) => r === "T").length;
    const total = results.length;
    const pct = (n) => (total ? Math.round((n / total) * 100) : 0);
    // current streak (non-tie)
    let streak = 0;
    let streakType = null;
    for (let i = results.length - 1; i >= 0; i--) {
      if (results[i] === "T") continue;
      if (streakType === null) {
        streakType = results[i];
        streak = 1;
      } else if (results[i] === streakType) streak++;
      else break;
    }
    return { b, p, t, total, pct, streak, streakType };
  }, [results]);

  const bigRoad = useMemo(() => buildBigRoad(results), [results]);

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-14">
      <p className="overline text-gold mb-3">▸ Q Branch // Surveillance</p>
      <h1 className="font-display text-4xl sm:text-5xl text-white tracking-tighter">Shoe Pattern Board</h1>
      <p className="mt-4 text-white/60 max-w-2xl leading-relaxed">
        Log every hand as it lands. Tap Banker, Player or Tie to record the bead plate and big road. Your
        session saves automatically — close the tab and return to it later.
      </p>

      {/* Input controls */}
      <div className="mt-10 grid sm:grid-cols-3 gap-3">
        {["B", "P", "T"].map((r) => (
          <button
            key={r}
            data-testid={`log-${META[r].label.toLowerCase()}`}
            onClick={() => add(r)}
            className={`group relative flex items-center justify-center gap-3 py-6 border border-white/15 bg-card hover:border-white/40 transition-colors active:scale-[0.98]`}
          >
            <span className={`w-9 h-9 rounded-full ${META[r].color} grid place-items-center text-white font-mono font-bold`}>
              {r}
            </span>
            <span className="font-display text-xl text-white">{META[r].label}</span>
          </button>
        ))}
      </div>

      <div className="mt-3 flex gap-3">
        <button
          data-testid="shoe-undo"
          onClick={undo}
          disabled={results.length === 0}
          className="flex-1 inline-flex items-center justify-center gap-2 py-3 border border-white/15 text-white/70 hover:text-white hover:border-white/40 transition-colors disabled:opacity-40 text-sm"
        >
          <Undo2 className="w-4 h-4" /> Undo
        </button>
        <button
          data-testid="shoe-reset"
          onClick={reset}
          disabled={results.length === 0}
          className="flex-1 inline-flex items-center justify-center gap-2 py-3 border border-crimson-bright/30 text-crimson-bright hover:bg-crimson/10 transition-colors disabled:opacity-40 text-sm"
        >
          <Trash2 className="w-4 h-4" /> Clear Shoe
        </button>
      </div>

      {/* Stats */}
      <div className="mt-6 grid grid-cols-2 sm:grid-cols-5 gap-px bg-white/10 border border-white/10" data-testid="shoe-stats">
        <StatCell label="Hands" value={stats.total} tone="gold" />
        <StatCell label="Banker" value={`${stats.b} · ${stats.pct(stats.b)}%`} tone="banker" />
        <StatCell label="Player" value={`${stats.p} · ${stats.pct(stats.p)}%`} tone="player" />
        <StatCell label="Tie" value={`${stats.t} · ${stats.pct(stats.t)}%`} tone="tie" />
        <StatCell
          label="Streak"
          value={stats.streakType ? `${META[stats.streakType].label.slice(0, 1)}×${stats.streak}` : "—"}
          tone="gold"
        />
      </div>

      {/* Roadmaps */}
      <div className="mt-8 grid lg:grid-cols-2 gap-6">
        {/* Bead Plate */}
        <div className="border border-white/10 bg-card">
          <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
            <span className="overline text-gold">Bead Plate</span>
            <span className="overline text-white/40">Every hand</span>
          </div>
          <div className="p-5 overflow-x-auto">
            {results.length === 0 ? (
              <EmptyGrid rows={BEAD_ROWS} />
            ) : (
              <div className="grid grid-flow-col gap-1.5 w-max" style={{ gridTemplateRows: `repeat(${BEAD_ROWS}, minmax(0, 1fr))` }} data-testid="bead-plate">
                {results.map((r, i) => (
                  <div
                    key={i}
                    className={`w-8 h-8 rounded-full ${META[r].color} grid place-items-center text-white text-xs font-mono font-bold`}
                    title={META[r].label}
                  >
                    {r}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Big Road */}
        <div className="border border-white/10 bg-card">
          <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
            <span className="overline text-gold">Big Road</span>
            <span className="overline text-white/40">Streaks · ties marked</span>
          </div>
          <div className="p-5 overflow-x-auto">
            {bigRoad.length === 0 ? (
              <EmptyGrid rows={BIG_ROWS} />
            ) : (
              <div className="flex gap-1.5 w-max" data-testid="big-road">
                {bigRoad.map((col, ci) => (
                  <div key={ci} className="flex flex-col gap-1.5">
                    {col.map((cell, ri) => (
                      <div key={ri} className="w-8 h-8 grid place-items-center">
                        <span
                          className={`w-7 h-7 rounded-full border-2 ${
                            cell.type === "B" ? "border-banker" : "border-player"
                          } grid place-items-center relative`}
                        >
                          {cell.ties > 0 && (
                            <span className="absolute inset-0 grid place-items-center">
                              <span className="w-full h-[2px] bg-tie rotate-45 rounded" />
                            </span>
                          )}
                          {cell.ties > 1 && (
                            <span className="absolute -top-1 -right-1 text-[9px] font-mono text-tie">{cell.ties}</span>
                          )}
                        </span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="mt-6 flex flex-wrap gap-5">
        {["B", "P", "T"].map((r) => (
          <div key={r} className="flex items-center gap-2">
            <Circle className={`w-3 h-3 ${META[r].text} fill-current`} />
            <span className="text-sm text-white/60">{META[r].label}</span>
          </div>
        ))}
      </div>

      <p className="mt-8 text-sm text-white/40 leading-relaxed max-w-2xl">
        New to roadmaps? Learn to read them in{" "}
        <a href="/dossier/reading-the-roadmaps" className="text-gold hover:underline">
          Reading the Roadmaps
        </a>
        . Remember: past results never influence future hands — each shoe is independent.
      </p>

      <div className="mt-10">
        <AdSlot size="728x90" label="Below Tool" />
      </div>
    </div>
  );
}

function buildBigRoad(results) {
  const cols = [];
  results.forEach((r) => {
    if (r === "T") {
      // attach tie to last placed cell if any
      const lastCol = cols[cols.length - 1];
      if (lastCol && lastCol.length) {
        lastCol[lastCol.length - 1].ties += 1;
      } else {
        cols.push([{ type: "T", ties: 1 }]);
      }
      return;
    }
    const lastCol = cols[cols.length - 1];
    if (!lastCol || lastCol[0].type !== r || lastCol[0].type === "T") {
      cols.push([{ type: r, ties: 0 }]);
    } else {
      lastCol.push({ type: r, ties: 0 });
    }
  });
  return cols;
}

const EmptyGrid = ({ rows }) => (
  <div className="grid grid-flow-col gap-1.5 opacity-30" style={{ gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))` }}>
    {Array.from({ length: rows * 8 }).map((_, i) => (
      <div key={i} className="w-8 h-8 rounded-full border border-white/15" />
    ))}
  </div>
);

const StatCell = ({ label, value, tone }) => {
  const toneCls =
    tone === "banker" ? "text-banker" : tone === "player" ? "text-player" : tone === "tie" ? "text-tie" : "text-gold";
  return (
    <div className="bg-background px-4 py-4">
      <div className="overline text-white/40">{label}</div>
      <div className={`mt-1.5 font-mono text-lg ${toneCls}`}>{value}</div>
    </div>
  );
};
